import axios from 'axios';

const API_URL = 'https://api.safecorepest.com/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
// SafeCore: auto-inject device-local timestamps in all writes (POST/PUT/PATCH)
// Backend requires local_date + local_time on every auditable write — this
// interceptor adds them transparently so individual components do not need
// to think about it. If the body already includes local_date/local_time
// (e.g. a Login screen that captures them at the moment of action), they win.
api.interceptors.request.use((config) => {
  const writeMethods = ['post', 'put', 'patch'];
  if (config.method && writeMethods.includes(config.method.toLowerCase())) {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const HH = String(now.getHours()).padStart(2, '0');
    const MI = String(now.getMinutes()).padStart(2, '0');
    const SS = String(now.getSeconds()).padStart(2, '0');
    const localDate = yyyy + '-' + mm + '-' + dd;
    const localTime = HH + ':' + MI + ':' + SS;

    // Only inject for JSON bodies (avoid touching FormData / multipart uploads)
    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      if (config.data.local_date === undefined) config.data.local_date = localDate;
      if (config.data.local_time === undefined) config.data.local_time = localTime;
    } else if (!config.data) {
      // No body provided — inject one so backend gets the timestamps
      config.data = { local_date: localDate, local_time: localTime };
    }
  }
  return config;
});



// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if we're not already there
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.success) {
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },
  
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    if (response.data.success) {
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }
};

// Company services
export const companyService = {
  getAll: async () => {
    const response = await api.get('/companies');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/companies/${id}`);
    return response.data;
  },
  
  create: async (companyData) => {
    const response = await api.post('/companies', companyData);
    return response.data;
  },
  
  update: async (id, companyData) => {
    const response = await api.put(`/companies/${id}`, companyData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await api.delete(`/companies/${id}`);
    return response.data;
  }
};

export default api;
